import {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import type {Plan, PlanFeature} from '@Panels/premium-pricing-panel/premium-pricing-panel.types';
import {PlanCycle} from '@Panels/premium-pricing-panel/premium-pricing-panel.types';
import {isMonthly} from '@Libraries/change-subscription-plan-library';
import type {SubscriptionPlan} from '@Panels/change-plan-modal/change-plan-modal.types';
import { PMW_FEATURE_CONSTANTS } from './premium-pricing-panel.constants';

function getPlanDescription(planLevel: USER_PREMIUM_LEVELS): string {
  if (planLevel === USER_PREMIUM_LEVELS.PREMIUM) {
    return window.i18next.t('pmwjs_get_more_powerful_design_tools_and_start_marketing_your_brand_like_a_pro');
  } else if (planLevel === USER_PREMIUM_LEVELS.PREMIUM_PLUS) {
    return window.i18next.t('pmwjs_all_about_growth_take_your_marketing_and_branding_to_new_heights');
  }

  return '';
}

function getPlanFeatures(planLevel: USER_PREMIUM_LEVELS, currencyCode: string): PlanFeature[] {
  if (planLevel === USER_PREMIUM_LEVELS.PREMIUM) {
    const features: PlanFeature[] = [
      {
        featureName: 'everything_free',
        icon: 'icon-check',
        description: window.i18next.t('pmwjs_everything_included_in_free')
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.AUTO_CAPTION,
        icon: 'icon-sparkle',
        description: window.i18next.t('pmwjs_create_and_edit_with_ai_prompts')
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.POST_FREE_DOWNLOAD,
        icon: 'icon-cloud-download',
        description: window.i18next.t('pmwjs_unlimited_downloads_no_watermark')
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.SCHEDULE_POST,
        icon: 'icon-clock-o',
        description: window.i18next.t('pmwjs_schedule_social_media_posts_and_emails')
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.TEXT_TO_IMAGE,
        icon: 'icon-ai-image',
        description: window.i18next.t('pmwjs_ai_images_ai_writer_ai_subtitles_and_more_ai_tools')
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM,
        icon: 'icon-event',
        description: window.i18next.t('pmwjs_attract_crowds_with_x_event_pages_per_month_and_shareable_calendar', { numEvents: 5 })
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM,
        icon: 'icon-social-media',
        description: window.i18next.t('pmwjs_publish_on_x_social_media_accounts', { numAccounts: 3 })
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.EMAIL_SENDS,
        icon: 'icon-envelope',
        description: window.i18next.t('pmwjs_send_x_trackable_emails_per_month', { numEmails: 3000 })
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.BRANDS,
        icon: 'icon-brands',
        description: window.i18next.t('pmwjs_your_own_brand_kit_and_custom_fonts')
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.SPELL_CHECK,
        icon: 'icon-spellcheck',
        description: window.i18next.t('pmwjs_never_miss_a_typo_with_spellcheck')
      },
    ];

    features.push({
      featureName: PMW_FEATURE_CONSTANTS.CREDITS,
      icon: 'icon-credits',
      description: window.i18next.t('pmwjs_x_new_credits_each_month', { numCredits: currencyCode === 'PKR' ? 40 : 100 }),
      tooltip: window.i18next.t('pmwjs_unused_credits_expire_at_end_of_month'),
    });
    return features;
  } else if (planLevel === USER_PREMIUM_LEVELS.PREMIUM_PLUS) {
    const features: PlanFeature[] = [
      {
        featureName: 'everything_premium',
        icon: 'icon-check',
        description: window.i18next.t('pmwjs_everything_included_in_premium')
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM_PLUS,
        icon: 'icon-event',
        description: window.i18next.t('pmwjs_unlimited_event_pages')
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.RECURRING_EVENTS,
        icon: 'icon-event',
        description: window.i18next.t('pmwjs_set_recurring_events')
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.AUTO_CAPTION,
        icon: 'icon-sparkle',
        description: window.i18next.t('pmwjs_more_credits_for_ai_prompts')
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.TEXT_TO_IMAGE,
        icon: 'icon-ai-image',
        description: window.i18next.t('pmwjs_more_custom_ai_images_highlighted')
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.BG_REMOVALS,
        icon: 'icon-remove-bg',
        description: window.i18next.t('pmwjs_more_ai_bg_removals_highlighted')
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM_PLUS,
        icon: 'icon-social-media',
        description: window.i18next.t('pmwjs_connect_unlimited_social_accounts')
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.EMAIL_SENDS,
        icon: 'icon-envelope',
        description: window.i18next.t('pmwjs_send_x_trackable_emails_per_month', { numEmails: 10000 })
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.MORE_BRANDS,
        icon: 'icon-brands',
        description: window.i18next.t('pmwjs_unlimited_brand_kits')
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.CUSTOM_DOMAIN_ENABLED,
        icon: 'icon-at',
        description: window.i18next.t('pmwjs_branded_email_addresses')
      },
    ];

    features.push({
      featureName: PMW_FEATURE_CONSTANTS.CREDITS,
      icon: 'icon-credits',
      description: window.i18next.t('pmwjs_x_new_credits_each_month', { numCredits: currencyCode === 'PKR' ? 80 : 250 }),
      tooltip: window.i18next.t('pmwjs_unused_credits_expire_at_end_of_month'),
    });
    return features;
  }

  return [];
}

export function getPlansToShow(plans: Plan[], selectedCycle: PlanCycle): Plan[] {
  return plans.filter((plan) => plan.cycle === selectedCycle).sort((a, b) => a.level - b.level);
}

const PREMIUM_PLUS_FEATURE_NAMES: ReadonlySet<string> = new Set([
  PMW_FEATURE_CONSTANTS.CONTENT_PLANNER,
  PMW_FEATURE_CONSTANTS.MORE_BRANDS,
  PMW_FEATURE_CONSTANTS.EDITOR_BRANDS,
  PMW_FEATURE_CONSTANTS.CUSTOM_DOMAIN_ENABLED,
  PMW_FEATURE_CONSTANTS.ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM_PLUS,
  PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM_PLUS,
  PMW_FEATURE_CONSTANTS.EVENTS_REMOVE_WATERMARK,
  PMW_FEATURE_CONSTANTS.RECURRING_EVENTS,
]);

export function getHighlightedLevelForFeature(featureName?: string): USER_PREMIUM_LEVELS {
  if (featureName !== undefined && featureName !== '' && PREMIUM_PLUS_FEATURE_NAMES.has(featureName)) {
    return USER_PREMIUM_LEVELS.PREMIUM_PLUS;
  }
  return USER_PREMIUM_LEVELS.PREMIUM;
}

export function mapSubscriptionPlanToPlan(subscriptionPlan: SubscriptionPlan): Plan {
  return {
    priceId: subscriptionPlan.price.id,
    name: subscriptionPlan.level === USER_PREMIUM_LEVELS.PREMIUM.valueOf() ? window.i18next.t('pmwjs_premium') : window.i18next.t('pmwjs_premium_plus'),
    description: getPlanDescription(subscriptionPlan.level),
    price: subscriptionPlan.tiers[0].displayPrice,
    currencyCode: subscriptionPlan.price.currencyCode,
    level: subscriptionPlan.level,
    features: getPlanFeatures(subscriptionPlan.level, subscriptionPlan.price.currencyCode),
    cycle: isMonthly(subscriptionPlan.duration) ? PlanCycle.MONTHLY : PlanCycle.YEARLY,
  };
}

export function getPremiumMonthlyPrice(plans: Plan[]): number {
  return getMonthlyPlanPrice(plans, true);
}

export function getPremiumPlusMonthlyPrice(plans: Plan[]): number {
  return getMonthlyPlanPrice(plans, false);
}

function getMonthlyPlanPrice(plans: Plan[], isPremium: boolean): number {
  const level = isPremium ? USER_PREMIUM_LEVELS.PREMIUM : USER_PREMIUM_LEVELS.PREMIUM_PLUS;
  for (const plan of plans) {
    if (plan.level === level && plan.cycle === PlanCycle.MONTHLY) {
      return plan.price;
    }
  }

  return 0;
}

export function getPremiumYearlyPrice(plans: Plan[]): number {
  for (const plan of plans) {
    if (plan.level === USER_PREMIUM_LEVELS.PREMIUM && plan.cycle === PlanCycle.YEARLY) {
      return plan.price;
    }
  }

  return 0;
}

export function getYearlyDiscountPercentage(plans: Plan[]): number {
  const yearlyPrice = getPremiumYearlyPrice(plans);
  const annualisedMonthlyPrice = getPremiumMonthlyPrice(plans) * PlanCycle.YEARLY;
  if (annualisedMonthlyPrice <= 0 || yearlyPrice <= 0) {
    return 0;
  }

  return Math.round((1 - yearlyPrice / annualisedMonthlyPrice) * 100);
}
